import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';

const MentorsList = () => {
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    const fetchMentors = async () => {
      try {
        const res = await fetch('/api/mentors/fetchMentors');
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.message || 'Failed to fetch mentors');
        }
        setMentors(data.mentors || []);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchMentors();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return mentors;
    return mentors.filter((m) => {
      const haystack = [
        m.name,
        m.currentRole,
        m.currentSector,
        ...(m.areasOfSpecialism || []),
        ...(m.bestAtAdviceOn || []),
      ]
        .filter(Boolean)
        .join(' ') 
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [mentors, query]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center p-8">
          <div className="w-12 h-12 border-4 border-[#02B491] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading mentors…</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center p-8">
          <p className="text-red-600 font-medium">{error}</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Browse mentors</h1>
        </div>

        <div className="mb-8">
          <div className="relative max-w-lg">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, role, industry, or specialism"
              className="w-full rounded-lg border border-gray-300 px-4 py-3 pr-10 focus:outline-none focus:ring-2 focus:ring-[#02B491] focus:border-transparent"
            />
            <svg className="w-5 h-5 text-gray-400 absolute right-3 top-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
            </svg>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center text-gray-600">No mentors match your search.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((m) => (
              <a
                key={m.id}
                href={`/mentor/${encodeURIComponent(m.name)}`}
                className="group bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-center gap-4">
                    {m.picture?.url ? (
                      <img src={m.picture.url} alt={m.name} className="w-14 h-14 rounded-full object-cover border" />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#02B491] to-[#029078] text-white flex items-center justify-center text-xl font-bold">
                        {m.name?.charAt(0)?.toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 truncate group-hover:text-[#026b5c]">{m.name}</h3>
                      {m.currentRole && (
                        <p className="text-sm text-gray-700 truncate">{m.currentRole}</p>
                      )}
                      {m.currentSector && (
                        <p className="text-sm text-gray-500 truncate">{m.currentSector}</p>
                      )}
                    </div>
                  </div>

                  {(m.areasOfSpecialism?.length || m.bestAtAdviceOn?.length) ? (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {(m.areasOfSpecialism?.length ? m.areasOfSpecialism : m.bestAtAdviceOn).slice(0, 4).map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-2.5 py-1 rounded-md text-xs font-medium bg-[#02B491]/10 border border-[#02B491]/20 text-[#026b5c]"
                        >
                          {tag}
                        </span>
                      ))}
                      {((m.areasOfSpecialism?.length || m.bestAtAdviceOn?.length) > 4) && (
                        <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-gray-50 border border-gray-200 text-gray-700">
                          +{(m.areasOfSpecialism?.length || m.bestAtAdviceOn?.length) - 4}
                        </span>
                      )}
                    </div>
                  ) : null}

                  {m.relevantRoleArea?.length ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {m.relevantRoleArea.slice(0, 4).map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-2.5 py-1 rounded-md text-xs font-medium bg-gray-50 border border-gray-200 text-gray-700"
                        >
                          {tag}
                        </span>
                      ))}
                      {m.relevantRoleArea.length > 4 && (
                        <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-gray-50 border border-gray-200 text-gray-700">
                          +{m.relevantRoleArea.length - 4}
                        </span>
                      )}
                    </div>
                  ) : null}
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MentorsList;


